<template>
  <dashboard-layout title="Reports">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search reports…" />
    </template>

    <stat-grid>
      <stat-card label="Total reports" :value="reports.length.toLocaleString('en-US')" icon="reports" accent />
      <stat-card label="Distinct users" :value="distinctUsers.toLocaleString('en-US')" icon="users" />
      <stat-card label="Report types" :value="typeCounts.length" icon="filetext" />
    </stat-grid>

    <data-panel flush>
      <template #title>Reports <span class="muted">({{ filteredReports.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="typeFilter" :options="typeOptions" />
      </template>

      <loading-state v-if="loading" label="Loading reports…" />
      <data-table
        v-else
        :columns="columns"
        :rows="filteredReports"
        row-key="id"
        clickable
        @row-click="openReport"
      >
        <template #cell-report="{ row }">
          <status-badge variant="danger" :label="row.report || '—'" />
        </template>
        <template #cell-message="{ row }">
          <span class="msg" :title="row.message">{{ truncateText(row.message, 100) }}</span>
        </template>
        <template #cell-timestamp="{ row }">
          <span class="nowrap">{{ formatTimestamp(row.timestamp) }}</span>
        </template>
        <template #cell-userId="{ row }">
          <router-link v-if="row.userId" :to="`/user/${row.userId}`" class="user-link" @click.stop>
            {{ truncateText(row.userId, 12) }}
          </router-link>
          <span v-else class="muted">—</span>
        </template>
        <template #empty>No reports match your filters</template>
      </data-table>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseReportService } from '../services/firebaseReportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'report', label: 'Report Type', width: '160px' },
  { key: 'message', label: 'Message' },
  { key: 'timestamp', label: 'Date', width: '150px' },
  { key: 'userId', label: 'User', width: '140px' }
]

export default {
  name: 'ReportDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseReportService()
    const reports = ref([])
    const searchQuery = ref('')
    const typeFilter = ref('all')
    const loading = ref(true)

    const loadReports = async () => {
      try {
        reports.value = await service.getAllReports()
      } catch (error) {
        console.error('Error loading reports:', error)
      } finally {
        loading.value = false
      }
    }

    const distinctUsers = computed(() => new Set(reports.value.map(r => r.userId).filter(Boolean)).size)

    const typeCounts = computed(() => {
      const counts = {}
      reports.value.forEach(r => {
        const t = r.report || 'unknown'
        counts[t] = (counts[t] || 0) + 1
      })
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }))
    })

    const typeOptions = computed(() => [
      { value: 'all', label: 'All', count: reports.value.length },
      ...typeCounts.value.map(t => ({ value: t.value, label: t.value, count: t.count }))
    ])

    const filteredReports = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      return reports.value.filter(r => {
        if (typeFilter.value !== 'all' && (r.report || 'unknown') !== typeFilter.value) return false
        if (q) {
          const hay = `${r.report || ''} ${r.message || ''} ${r.userId || ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const truncateText = (text, maxLength) => {
      if (!text) return '-'
      return text.length > maxLength ? text.substring(0, maxLength) + '…' : text
    }

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '—'
      const ms = timestamp.seconds ? timestamp.seconds * 1000 : new Date(timestamp).getTime()
      if (isNaN(ms)) return '—'
      return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const openReport = (row, event) => {
      if (event.metaKey || event.ctrlKey) window.open(`/report/${row.id}`, '_blank')
      else router.push(`/report/${row.id}`)
    }

    onMounted(loadReports)

    return {
      reports, searchQuery, typeFilter, loading, columns,
      distinctUsers, typeCounts, typeOptions, filteredReports,
      truncateText, formatTimestamp, openReport
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }
.msg {
  display: inline-block;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
</style>
